import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../../supabaseClient';
import Select from '../Select';
import { normalizePhone } from '../../../utils/phone';
import { normalizeEmail } from '../../../utils/email';
import { logAudit, diffFields } from '../../../utils/audit';
import {
  sendConfirmationEmailsForIds,
  sendConfirmationEmailsForRegistration,
} from '../../../utils/registrationEmail';
import { syncTeamPlayerHandicap } from '../../../utils/golfTeams';
import { paymentStatusLabel, paymentStatusClasses } from '../../../utils/paymentStatus';

const REGISTRATION_FIELDS = ['contact_id', 'golf_handicap', 'preferred_teammates'];

// Log a contact.created audit entry for a contact inserted from the registration form.
const logContactCreated = (contact) =>
  logAudit({
    action: 'contact.created',
    entityType: 'contact',
    entityId: contact.id,
    entityLabel: `${contact.first_name} ${contact.last_name}`,
    changes: {
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      phone: contact.phone,
    },
  });

const createBlankAttendee = () => ({
  contact_id: '',
  contactSearchTerm: '',
  showContactDropdown: false,
  showNewContactForm: false,
  newContactData: { first_name: '', last_name: '', email: '', phone: '' },
  golf_handicap: '',
  preferred_teammates: '',
  selectedEvents: {},
  childCounts: {},
});

// A single attendee card (contact select/create, handicap,
// preferred teammates, event selection, child counts). Shared between create mode
// (registering several people at once) and the edit-mode "add to this group"
// section. All state lives in the parent; this is purely presentational.
function AttendeeCard({
  attendee,
  index,
  events,
  filteredContacts,
  onUpdate,
  onRemove,
  onCreateContact,
  loading,
  canRemove,
  label,
}) {
  const idPrefix = `att-${label.replace(/\s+/g, '-').toLowerCase()}-${index}`;
  return (
    <div className="border border-gray-200 dark:border-night-700 rounded-lg p-4 space-y-4">
      {/* Card header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {label} {index + 1}
        </h3>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="text-sm text-red-600 hover:text-red-700 font-medium"
          >
            Remove
          </button>
        )}
      </div>

      {/* Contact Selection */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Contact <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={() => onUpdate(index, { showNewContactForm: !attendee.showNewContactForm })}
            className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:text-primary-300 font-medium"
          >
            {attendee.showNewContactForm ? 'Cancel' : '+ New Contact'}
          </button>
        </div>

        {attendee.showNewContactForm ? (
          <div className="bg-gray-50 dark:bg-night-700 p-4 rounded-md space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={attendee.newContactData.first_name}
                  onChange={(e) => onUpdate(index, {
                    newContactData: { ...attendee.newContactData, first_name: e.target.value }
                  })}
                  className="block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={attendee.newContactData.last_name}
                  onChange={(e) => onUpdate(index, {
                    newContactData: { ...attendee.newContactData, last_name: e.target.value }
                  })}
                  className="block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input
                type="email"
                spellCheck={false}
                value={attendee.newContactData.email}
                onChange={(e) => onUpdate(index, {
                  newContactData: { ...attendee.newContactData, email: e.target.value }
                })}
                className="block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
              <input
                type="tel"
                spellCheck={false}
                value={attendee.newContactData.phone}
                onChange={(e) => onUpdate(index, {
                  newContactData: { ...attendee.newContactData, phone: e.target.value }
                })}
                className="block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => onCreateContact(index)}
              disabled={loading}
              className="w-full px-3 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              Create and Select
            </button>
          </div>
        ) : (
          <div className="relative">
            <input
              type="text"
              value={attendee.contactSearchTerm}
              onChange={(e) => onUpdate(index, {
                contactSearchTerm: e.target.value,
                showContactDropdown: true,
                contact_id: '',
              })}
              onFocus={() => onUpdate(index, { showContactDropdown: true })}
              onBlur={() => setTimeout(() => onUpdate(index, { showContactDropdown: false }), 200)}
              placeholder="Search by name or email..."
              spellCheck={false}
              className="mt-1 block w-full"
              autoComplete="off"
            />
            {attendee.showContactDropdown && attendee.contactSearchTerm && (
              <div className="absolute z-10 mt-1 w-full bg-white dark:bg-night-800 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                {filteredContacts.length > 0 ? (
                  filteredContacts.map((contact) => (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => onUpdate(index, {
                        contact_id: contact.id,
                        contactSearchTerm: `${contact.first_name} ${contact.last_name}`,
                        showContactDropdown: false,
                      })}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:bg-night-900 focus:bg-gray-100 dark:bg-night-900 focus:outline-none"
                    >
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {contact.first_name} {contact.last_name}
                      </div>
                      {contact.email && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">{contact.email}</div>
                      )}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                    No contacts found
                  </div>
                )}
              </div>
            )}
            {!attendee.contact_id && attendee.contactSearchTerm && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Please select a contact from the dropdown
              </p>
            )}
          </div>
        )}
      </div>

      {/* Golf Handicap */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Golf Handicap
          {events.some(ev => ev.event_type === 'golf_tournament' && attendee.selectedEvents[ev.id]) && (
            <span className="text-red-500 ml-1">*</span>
          )}
        </label>
        <input
          type="number"
          step="0.1"
          value={attendee.golf_handicap}
          onChange={(e) => onUpdate(index, { golf_handicap: e.target.value })}
          placeholder={events.some(ev => ev.event_type === 'golf_tournament' && attendee.selectedEvents[ev.id]) ? "Required for golf (e.g., 18.5)" : "e.g., 18.5"}
          className="mt-1 block w-full"
        />
      </div>

      {/* Preferred Teammates */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Preferred Teammates
        </label>
        <textarea
          rows={2}
          value={attendee.preferred_teammates}
          onChange={(e) => onUpdate(index, { preferred_teammates: e.target.value })}
          placeholder="Names of preferred golf teammates"
          className="mt-1 block w-full"
        />
      </div>

      {/* Events Attending */}
      {events.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Events Attending
          </label>
          <div className="space-y-2 bg-gray-50 dark:bg-night-700 p-4 rounded-md">
            {events.map((event) => (
              <div key={event.id} className="flex items-center">
                <input
                  type="checkbox"
                  id={`${idPrefix}-select-event-${event.id}`}
                  checked={attendee.selectedEvents[event.id] || false}
                  onChange={() => onUpdate(index, {
                    selectedEvents: {
                      ...attendee.selectedEvents,
                      [event.id]: !attendee.selectedEvents[event.id],
                    },
                  })}
                  className="h-4 w-4 text-primary-600 dark:text-primary-400 focus:ring-primary-500 border-gray-300 dark:border-night-600 rounded"
                />
                <label htmlFor={`${idPrefix}-select-event-${event.id}`} className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                  {event.event_name}
                </label>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Select which events this attendee is registered for
          </p>
        </div>
      )}

      {/* Child Counts by Event */}
      {events.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Child Counts by Event
          </label>
          <div className="space-y-3 bg-gray-50 dark:bg-night-700 p-4 rounded-md">
            {events.map((event) => (
              <div key={event.id} className="flex items-center justify-between">
                <label htmlFor={`${idPrefix}-event-${event.id}`} className="text-sm text-gray-700 dark:text-gray-300">
                  {event.event_name}
                </label>
                <input
                  type="number"
                  id={`${idPrefix}-event-${event.id}`}
                  min="0"
                  value={attendee.childCounts[event.id] || 0}
                  onChange={(e) => onUpdate(index, {
                    childCounts: {
                      ...attendee.childCounts,
                      [event.id]: e.target.value,
                    },
                  })}
                  className="w-20 rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Enter the number of children attending each event
          </p>
        </div>
      )}
    </div>
  );
}

export default function RegistrationEditForm({ registration, onClose, onSave }) {
  // --- Shared state ---
  const [contacts, setContacts] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isCreateMode = !registration;

  // --- Edit mode state (also used for tournament_id in create mode) ---
  const [formData, setFormData] = useState({
    contact_id: '',
    tournament_id: '',
    payment_status: 'pending',
    golf_handicap: '',
    preferred_teammates: '',
  });
  const [selectedEvents, setSelectedEvents] = useState({});
  const [registrationEvents, setRegistrationEvents] = useState({});
  // Snapshot of which events were selected when the form loaded, used to
  // detect an added/removed event (vs. just a child-count edit) at save time.
  const [originalSelectedEvents, setOriginalSelectedEvents] = useState({});
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContactData, setNewContactData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);

  // --- Create mode multi-attendee state ---
  const [attendees, setAttendees] = useState([createBlankAttendee()]);
  // Create mode: whether to send the confirmation email(s) on save. On by
  // default, matching the previous always-send behavior.
  const [emailContacts, setEmailContacts] = useState(true);

  // --- Edit mode: registrants being added to this registration's group ---
  const [groupAttendees, setGroupAttendees] = useState([]);

  useEffect(() => {
    // Always fetch contacts for both create and edit modes
    fetchContacts();

    if (isCreateMode) {
      // Fetch tournaments for create mode
      fetchTournaments();
    } else {
      // Edit mode - populate with existing data
      setFormData({
        contact_id: registration.contact_id || '',
        tournament_id: registration.tournament_id || '',
        payment_status: registration.payment_status || 'pending',
        golf_handicap: registration.golf_handicap || '',
        preferred_teammates: registration.preferred_teammates || '',
      });

      // Set initial search term to current contact name
      if (registration.first_name && registration.last_name) {
        setContactSearchTerm(`${registration.first_name} ${registration.last_name}`);
      }

      fetchEventsForRegistration();
    }
  }, [registration, isCreateMode]);

  // Fetch events when tournament is selected in create mode
  useEffect(() => {
    if (isCreateMode && formData.tournament_id) {
      fetchEventsForTournament(formData.tournament_id);
    }
  }, [formData.tournament_id, isCreateMode]);

  const fetchContacts = async () => {
    try {
      // Fetch all contacts
      const { data: contactsData, error: contactsError } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email')
        .order('last_name');

      if (contactsError) throw contactsError;
      setContacts(contactsData || []);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError(err.message || 'Failed to load contacts');
    }
  };

  const fetchTournaments = async () => {
    try {
      // Fetch all tournaments
      const { data: tournamentsData, error: tournamentsError } = await supabase
        .from('tournaments')
        .select('id, year')
        .order('year', { ascending: false });

      if (tournamentsError) throw tournamentsError;
      setTournaments(tournamentsData || []);

      // Set default tournament to most recent in create mode
      if (isCreateMode && tournamentsData && tournamentsData.length > 0) {
        setFormData(prev => ({ ...prev, tournament_id: tournamentsData[0].id }));
      }
    } catch (err) {
      console.error('Error fetching tournaments:', err);
      setError(err.message || 'Failed to load tournaments');
    }
  };

  const fetchEventsForTournament = async (tournamentId) => {
    try {
      const { data: tournamentEvents, error: eventsError } = await supabase
        .from('tournament_events')
        .select('id, event_name, event_type')
        .eq('tournament_id', tournamentId)
        .order('event_date');

      if (eventsError) throw eventsError;
      setEvents(tournamentEvents || []);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  // === Edit mode contact functions (unchanged) ===

  const handleCreateNewContact = async () => {
    // Validate new contact data
    if (!newContactData.first_name || !newContactData.last_name) {
      setError('First name and last name are required for new contact');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Check if contact with this email already exists (case-insensitive).
      if (newContactData.email) {
        const { data: existingContact, error: searchError } = await supabase
          .from('contacts')
          .select('*')
          .ilike('email', normalizeEmail(newContactData.email))
          .maybeSingle();

        if (existingContact && !searchError) {
          // Contact already exists, select it instead
          handleSelectContact(existingContact);
          setNewContactData({ first_name: '', last_name: '', email: '', phone: '' });
          setShowNewContactForm(false);
          setError(null);
          alert(`Contact with email ${newContactData.email} already exists. Selected existing contact: ${existingContact.first_name} ${existingContact.last_name}`);
          return;
        }
      }

      // Create new contact
      const { data: newContact, error: insertError } = await supabase
        .from('contacts')
        .insert({
          first_name: newContactData.first_name.trim(),
          last_name: newContactData.last_name.trim(),
          email: normalizeEmail(newContactData.email) || null,
          phone: normalizePhone(newContactData.phone),
        })
        .select()
        .single();

      if (insertError) throw insertError;

      await logContactCreated(newContact);

      // Refresh contacts list
      await fetchContacts();

      // Select the newly created contact
      handleSelectContact(newContact);

      // Reset form and hide
      setNewContactData({ first_name: '', last_name: '', email: '', phone: '' });
      setShowNewContactForm(false);
      setError(null);
    } catch (err) {
      console.error('Error creating contact:', err);

      // Handle duplicate email error specifically
      if (err.message && err.message.includes('contacts_email_key')) {
        setError(`A contact with email "${newContactData.email}" already exists. Please search for and select the existing contact instead.`);
      } else {
        setError(err.message || 'Failed to create contact');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectContact = (contact) => {
    setFormData(prev => ({ ...prev, contact_id: contact.id }));
    setContactSearchTerm(`${contact.first_name} ${contact.last_name}`);
    setShowContactDropdown(false);
  };

  const handleContactSearchChange = (value) => {
    setContactSearchTerm(value);
    setShowContactDropdown(true);
    // Clear selection if user is typing
    if (value !== contactSearchTerm) {
      setFormData(prev => ({ ...prev, contact_id: '' }));
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const searchLower = contactSearchTerm.toLowerCase();
    const fullName = `${contact.first_name} ${contact.last_name}`.toLowerCase();
    const email = contact.email?.toLowerCase() || '';
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  const fetchEventsForRegistration = async () => {
    try {
      // Fetch all events for this tournament
      const { data: tournamentEvents, error: eventsError } = await supabase
        .from('tournament_events')
        .select('id, event_name, event_type')
        .eq('tournament_id', registration.tournament_id)
        .order('event_date');

      if (eventsError) throw eventsError;
      setEvents(tournamentEvents || []);

      // Fetch existing registration_events
      const { data: regEvents, error: regEventsError } = await supabase
        .from('registration_events')
        .select('tournament_event_id, child_count')
        .eq('registration_id', registration.registration_id);

      if (regEventsError) throw regEventsError;

      // Convert to maps for easier access
      const eventsMap = {};
      const selectedMap = {};
      regEvents.forEach(re => {
        eventsMap[re.tournament_event_id] = re.child_count;
        selectedMap[re.tournament_event_id] = true; // Event is selected if registration_event exists
      });
      setRegistrationEvents(eventsMap);
      setSelectedEvents(selectedMap);
      setOriginalSelectedEvents(selectedMap);
    } catch (err) {
      console.error('Error fetching registration events:', err);
    }
  };

  const handleEventToggle = (eventId) => {
    setSelectedEvents({
      ...selectedEvents,
      [eventId]: !selectedEvents[eventId],
    });
  };

  const handleChildCountChange = (eventId, value) => {
    setRegistrationEvents({
      ...registrationEvents,
      [eventId]: value,
    });
  };

  // === Multi-attendee helpers (shared by create mode and edit-mode group) ===

  // Generic list mutators, parameterized by which setter (create vs group list).
  const updateInList = (setList) => (index, updates) => {
    setList(prev => prev.map((a, i) => i === index ? { ...a, ...updates } : a));
  };
  const removeInList = (setList) => (index) => {
    setList(prev => prev.filter((_, i) => i !== index));
  };

  const updateAttendee = updateInList(setAttendees);
  const removeAttendee = removeInList(setAttendees);
  const addAttendee = () => setAttendees(prev => [...prev, createBlankAttendee()]);

  const updateGroupAttendee = updateInList(setGroupAttendees);
  const removeGroupAttendee = removeInList(setGroupAttendees);
  const addGroupAttendee = () => setGroupAttendees(prev => [...prev, createBlankAttendee()]);

  // Create (or select an existing) contact for one attendee. `applyUpdate` writes
  // the resolved contact back into whichever list the attendee lives in.
  const createContactForAttendee = async (attendee, applyUpdate) => {
    const ncd = attendee.newContactData;

    if (!ncd.first_name || !ncd.last_name) {
      setError('First name and last name are required for new contact');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (ncd.email) {
        const { data: existingContact, error: searchError } = await supabase
          .from('contacts')
          .select('*')
          .ilike('email', normalizeEmail(ncd.email))
          .maybeSingle();

        if (existingContact && !searchError) {
          applyUpdate({
            contact_id: existingContact.id,
            contactSearchTerm: `${existingContact.first_name} ${existingContact.last_name}`,
            showContactDropdown: false,
            newContactData: { first_name: '', last_name: '', email: '', phone: '' },
            showNewContactForm: false,
          });
          setError(null);
          alert(`Contact with email ${ncd.email} already exists. Selected existing contact: ${existingContact.first_name} ${existingContact.last_name}`);
          return;
        }
      }

      const { data: newContact, error: insertError } = await supabase
        .from('contacts')
        .insert({
          first_name: ncd.first_name.trim(),
          last_name: ncd.last_name.trim(),
          email: normalizeEmail(ncd.email) || null,
          phone: normalizePhone(ncd.phone),
        })
        .select()
        .single();

      if (insertError) throw insertError;

      await logContactCreated(newContact);

      await fetchContacts();

      applyUpdate({
        contact_id: newContact.id,
        contactSearchTerm: `${newContact.first_name} ${newContact.last_name}`,
        showContactDropdown: false,
        newContactData: { first_name: '', last_name: '', email: '', phone: '' },
        showNewContactForm: false,
      });
      setError(null);
    } catch (err) {
      console.error('Error creating contact:', err);
      if (err.message && err.message.includes('contacts_email_key')) {
        setError(`A contact with email "${ncd.email}" already exists. Please search for and select the existing contact instead.`);
      } else {
        setError(err.message || 'Failed to create contact');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewContactForAttendee = (index) =>
    createContactForAttendee(attendees[index], (u) => updateAttendee(index, u));

  const handleCreateNewContactForGroup = (index) =>
    createContactForAttendee(groupAttendees[index], (u) => updateGroupAttendee(index, u));

  const getFilteredContactsFor = (searchTerm) => {
    const searchLower = searchTerm.toLowerCase();
    return contacts.filter(contact => {
      const fullName = `${contact.first_name} ${contact.last_name}`.toLowerCase();
      const email = contact.email?.toLowerCase() || '';
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  };

  // === Submit handler ===

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isCreateMode) {
        // --- CREATE MODE: Multiple attendees ---

        // Validate all attendees
        for (let i = 0; i < attendees.length; i++) {
          const att = attendees[i];

          if (!att.contact_id) {
            setError(`Attendee ${i + 1}: Please select a contact`);
            setLoading(false);
            return;
          }

          const attSelectedEvents = events.filter(ev => att.selectedEvents[ev.id]);
          if (attSelectedEvents.length === 0) {
            setError(`Attendee ${i + 1}: Please select at least one event`);
            setLoading(false);
            return;
          }

          const isGolfSelected = attSelectedEvents.some(ev => ev.event_type === 'golf_tournament');
          if (isGolfSelected && !att.golf_handicap) {
            setError(`Attendee ${i + 1}: Golf handicap is required when golf tournament is selected. Enter 20 if you don't have one.`);
            setLoading(false);
            return;
          }
        }

        // Generate group ID if multiple attendees
        const groupId = attendees.length > 1 ? crypto.randomUUID() : null;

        // Insert all registrations
        const insertedIds = [];
        for (const att of attendees) {
          const { data: newReg, error: insertError } = await supabase
            .from('registrations')
            .insert({
              contact_id: att.contact_id,
              tournament_id: formData.tournament_id,
              payment_status: 'pending',
              golf_handicap: att.golf_handicap || null,
              preferred_teammates: att.preferred_teammates || null,
              registration_group_id: groupId,
            })
            .select()
            .single();

          if (insertError) throw insertError;
          insertedIds.push(newReg.id);

          // Insert registration_events for this attendee
          const attEventNames = [];
          for (const event of events) {
            if (att.selectedEvents[event.id]) {
              const { error: insertEvError } = await supabase
                .from('registration_events')
                .insert({
                  registration_id: newReg.id,
                  tournament_event_id: event.id,
                  child_count: parseInt(att.childCounts[event.id]) || 0,
                });

              if (insertEvError) throw insertEvError;
              attEventNames.push(event.event_name);
            }
          }

          await logAudit({
            action: 'registration.created',
            entityType: 'registration',
            entityId: newReg.id,
            entityLabel: att.contactSearchTerm || 'Registration',
            changes: {
              payment_status: 'pending',
              golf_handicap: att.golf_handicap || null,
              events: attEventNames.join(', ') || null,
            },
            metadata: groupId ? { registration_group_id: groupId } : undefined,
          });
        }

        // Send confirmation emails, same as a public registration, unless the
        // admin unchecked the box. A failure here shouldn't fail the
        // registration that was just created.
        if (emailContacts) {
          try {
            await sendConfirmationEmailsForIds(insertedIds, formData.tournament_id);
          } catch (emailErr) {
            console.error('Failed to send registration confirmation email:', emailErr);
          }
        }

        onSave();
        onClose();
      } else {
        // --- EDIT MODE: Single registration (unchanged) ---

        const selectedEventTypes = events
          .filter(event => selectedEvents[event.id])
          .map(event => event.event_type);

        if (selectedEventTypes.length === 0) {
          setError('Please select at least one event');
          setLoading(false);
          return;
        }

        if (!formData.contact_id) {
          setError('Please select a contact');
          setLoading(false);
          return;
        }

        const isGolfSelected = selectedEventTypes.includes('golf_tournament');
        if (isGolfSelected && !formData.golf_handicap) {
          setError('Golf handicap is required when golf tournament is selected. Enter 20 if you don\'t have one.');
          setLoading(false);
          return;
        }

        const registrationId = registration.registration_id;

        const registrationUpdate = {
          contact_id: formData.contact_id,
          golf_handicap: formData.golf_handicap || null,
          preferred_teammates: formData.preferred_teammates || null,
        };
        const { error: updateError } = await supabase
          .from('registrations')
          .update(registrationUpdate)
          .eq('id', registrationId);

        if (updateError) throw updateError;

        // Push the handicap through to any golf team this player is already on
        // so the Team Builder / team list / leaderboard don't show a stale
        // snapshot from when the team was built.
        await syncTeamPlayerHandicap({
          tournamentId: registration.tournament_id,
          contactId: formData.contact_id,
          handicap: formData.golf_handicap,
        });

        const registrationChanges = diffFields(
          {
            contact_id: registration.contact_id,
            golf_handicap: registration.golf_handicap,
            preferred_teammates: registration.preferred_teammates,
          },
          registrationUpdate,
          REGISTRATION_FIELDS
        );
        if (registrationChanges) {
          await logAudit({
            action: 'registration.updated',
            entityType: 'registration',
            entityId: registrationId,
            entityLabel: `${registration.first_name} ${registration.last_name}`,
            changes: registrationChanges,
          });
        }

        // Handle registration_events for each tournament event
        for (const event of events) {
          const isSelected = selectedEvents[event.id];
          const childCount = parseInt(registrationEvents[event.id]) || 0;

          // Check if registration_event exists
          const { data: existing, error: checkError } = await supabase
            .from('registration_events')
            .select('*')
            .eq('registration_id', registrationId)
            .eq('tournament_event_id', event.id)
            .single();

          if (checkError && checkError.code !== 'PGRST116') {
            throw checkError;
          }

          if (isSelected) {
            if (existing) {
              const { error: updateEvError } = await supabase
                .from('registration_events')
                .update({ child_count: childCount })
                .eq('registration_id', registrationId)
                .eq('tournament_event_id', event.id);

              if (updateEvError) throw updateEvError;
            } else {
              const { error: insertEvError } = await supabase
                .from('registration_events')
                .insert({
                  registration_id: registrationId,
                  tournament_event_id: event.id,
                  child_count: childCount,
                });

              if (insertEvError) throw insertEvError;
            }
          } else {
            if (existing) {
              const { error: deleteError } = await supabase
                .from('registration_events')
                .delete()
                .eq('registration_id', registrationId)
                .eq('tournament_event_id', event.id);

              if (deleteError) throw deleteError;
            }
          }
        }

        // Did the set of registered events change (an event added or
        // removed)? A child-count-only edit doesn't count — that's compared
        // against the original snapshot captured when the form loaded.
        const originalEventIds = new Set(Object.keys(originalSelectedEvents).filter(id => originalSelectedEvents[id]));
        const currentEventIds = new Set(events.filter(ev => selectedEvents[ev.id]).map(ev => String(ev.id)));
        const eventsChanged =
          originalEventIds.size !== currentEventIds.size ||
          [...originalEventIds].some(id => !currentEventIds.has(id));

        // --- Add any new registrants to this registration's group ---
        let groupId = registration.registration_group_id || null;
        if (groupAttendees.length > 0) {
          // Validate each new group member.
          for (let i = 0; i < groupAttendees.length; i++) {
            const att = groupAttendees[i];
            if (!att.contact_id) {
              setError(`New registrant ${i + 1}: Please select a contact`);
              setLoading(false);
              return;
            }
            const attSelectedEvents = events.filter(ev => att.selectedEvents[ev.id]);
            if (attSelectedEvents.length === 0) {
              setError(`New registrant ${i + 1}: Please select at least one event`);
              setLoading(false);
              return;
            }
            const golfSelected = attSelectedEvents.some(ev => ev.event_type === 'golf_tournament');
            if (golfSelected && !att.golf_handicap) {
              setError(`New registrant ${i + 1}: Golf handicap is required when golf tournament is selected. Enter 20 if you don't have one.`);
              setLoading(false);
              return;
            }
          }

          // Reuse the existing group id, or create one and back-fill the edited
          // registration so the original + new members share a group.
          if (!groupId) {
            groupId = crypto.randomUUID();
            const { error: groupErr } = await supabase
              .from('registrations')
              .update({ registration_group_id: groupId })
              .eq('id', registrationId);
            if (groupErr) throw groupErr;
          }

          // Insert each new registrant into the group (mirrors create mode).
          for (const att of groupAttendees) {
            const { data: newReg, error: insErr } = await supabase
              .from('registrations')
              .insert({
                contact_id: att.contact_id,
                tournament_id: registration.tournament_id,
                payment_status: 'pending',
                golf_handicap: att.golf_handicap || null,
                preferred_teammates: att.preferred_teammates || null,
                registration_group_id: groupId,
              })
              .select()
              .single();
            if (insErr) throw insErr;

            for (const event of events) {
              if (att.selectedEvents[event.id]) {
                const { error: evErr } = await supabase
                  .from('registration_events')
                  .insert({
                    registration_id: newReg.id,
                    tournament_event_id: event.id,
                    child_count: parseInt(att.childCounts[event.id]) || 0,
                  });
                if (evErr) throw evErr;
              }
            }
          }

          await logAudit({
            action: 'registration.group_member_added',
            entityType: 'registration',
            entityId: registrationId,
            entityLabel: `${registration.first_name} ${registration.last_name}`,
            changes: { added_registrants: groupAttendees.length },
          });

          // New members were just added to the group — email the whole
          // (updated) group, same as a fresh group registration. This already
          // reflects this registrant's current events too, so no separate
          // solo send is needed below. A failure here shouldn't fail the save.
          try {
            await sendConfirmationEmailsForRegistration(
              { id: registrationId, registration_group_id: groupId },
              registration.tournament_id
            );
          } catch (emailErr) {
            console.error('Failed to send registration confirmation email:', emailErr);
          }
        } else if (eventsChanged) {
          // Only this registrant's own events changed (added/removed, not
          // just a child-count edit) — email just them, not their whole
          // group. A failure here shouldn't fail the save.
          try {
            await sendConfirmationEmailsForIds([registrationId], registration.tournament_id);
          } catch (emailErr) {
            console.error('Failed to send registration confirmation email:', emailErr);
          }
        }

        onSave();
        onClose();
      }
    } catch (err) {
      console.error('Error saving registration:', err);
      setError(err.message || 'Failed to save registration');
    } finally {
      setLoading(false);
    }
  };

  // === Render ===

  // Portal: keeps `fixed inset-0` clear of the caller's space-y-* sibling margin.
  return createPortal(
    <div className="admin-content fixed inset-0 bg-gray-500 bg-opacity-75 flex items-start sm:items-center justify-center p-4 overflow-y-auto z-50">
      <div className="bg-white dark:bg-night-800 rounded-lg shadow-xl max-w-2xl w-full modal-panel overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-night-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {isCreateMode
              ? `New Registration${attendees.length > 1 ? ` (${attendees.length} attendees)` : ''}`
              : 'Edit Registration'
            }
          </h2>
          {!isCreateMode && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Tournament: {registration.tournament_year}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {isCreateMode ? (
            /* ========== CREATE MODE: Multi-attendee ========== */
            <div className="space-y-4">
              {/* Tournament Selection (shared) */}
              <div>
                <label htmlFor="tournament_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tournament <span className="text-red-500">*</span>
                </label>
                <Select
                  id="tournament_id"
                  required
                  value={formData.tournament_id}
                  onChange={(e) => setFormData({ ...formData, tournament_id: e.target.value })}
                  className="mt-1 block w-full"
                >
                  <option value="">Select a tournament...</option>
                  {tournaments.map((tournament) => (
                    <option key={tournament.id} value={tournament.id}>
                      {tournament.year}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Attendee cards */}
              {attendees.map((attendee, index) => (
                <AttendeeCard
                  key={index}
                  attendee={attendee}
                  index={index}
                  events={events}
                  filteredContacts={getFilteredContactsFor(attendee.contactSearchTerm)}
                  onUpdate={updateAttendee}
                  onRemove={removeAttendee}
                  onCreateContact={handleCreateNewContactForAttendee}
                  loading={loading}
                  canRemove={attendees.length > 1}
                  label="Attendee"
                />
              ))}

              {/* Add Another Attendee button */}
              <button
                type="button"
                onClick={addAttendee}
                className="w-full py-2 px-4 border-2 border-dashed border-gray-300 dark:border-night-600 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:border-gray-400 hover:text-gray-700 dark:text-gray-300 transition-colors"
              >
                + Add Another Attendee
              </button>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                New registrations start with a pending payment status. It updates when you record a payment.
              </p>

              {/* Email confirmation opt-out */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="email_contacts"
                  checked={emailContacts}
                  onChange={(e) => setEmailContacts(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-primary-600 dark:text-primary-400 focus:ring-primary-500 border-gray-300 dark:border-night-600 rounded"
                />
                <label htmlFor="email_contacts" className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                  Email the registration confirmation to {attendees.length > 1 ? 'each contact' : 'the contact'}
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    Uncheck to add the registration without sending any email.
                  </span>
                </label>
              </div>
            </div>
          ) : (
            /* ========== EDIT MODE: Single registration (unchanged) ========== */
            <div className="space-y-4">
              {/* Contact Selection - shown in both create and edit modes */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="contact_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Contact <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowNewContactForm(!showNewContactForm)}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:text-primary-300 font-medium"
                  >
                    {showNewContactForm ? 'Cancel' : '+ New Contact'}
                  </button>
                </div>

                {showNewContactForm ? (
                  <div className="bg-gray-50 dark:bg-night-700 p-4 rounded-md space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={newContactData.first_name}
                          onChange={(e) => setNewContactData({ ...newContactData, first_name: e.target.value })}
                          className="block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={newContactData.last_name}
                          onChange={(e) => setNewContactData({ ...newContactData, last_name: e.target.value })}
                          className="block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                      <input
                        type="email"
                        spellCheck={false}
                        value={newContactData.email}
                        onChange={(e) => setNewContactData({ ...newContactData, email: e.target.value })}
                        className="block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                      <input
                        type="tel"
                        spellCheck={false}
                        value={newContactData.phone}
                        onChange={(e) => setNewContactData({ ...newContactData, phone: e.target.value })}
                        className="block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleCreateNewContact}
                      disabled={loading}
                      className="w-full px-3 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    >
                      Create and Select
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      id="contact_id"
                      required
                      value={contactSearchTerm}
                      onChange={(e) => handleContactSearchChange(e.target.value)}
                      onFocus={() => setShowContactDropdown(true)}
                      onBlur={() => setTimeout(() => setShowContactDropdown(false), 200)}
                      placeholder="Search by name or email..."
                      spellCheck={false}
                      className="mt-1 block w-full"
                      autoComplete="off"
                    />
                    {showContactDropdown && contactSearchTerm && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-night-800 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {filteredContacts.length > 0 ? (
                          filteredContacts.map((contact) => (
                            <button
                              key={contact.id}
                              type="button"
                              onClick={() => handleSelectContact(contact)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:bg-night-900 focus:bg-gray-100 dark:bg-night-900 focus:outline-none"
                            >
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {contact.first_name} {contact.last_name}
                              </div>
                              {contact.email && (
                                <div className="text-sm text-gray-500 dark:text-gray-400">{contact.email}</div>
                              )}
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                            No contacts found
                          </div>
                        )}
                      </div>
                    )}
                    {!formData.contact_id && contactSearchTerm && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Please select a contact from the dropdown
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Payment Status</span>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${paymentStatusClasses(formData.payment_status)}`}>
                    {paymentStatusLabel(formData.payment_status)}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Updates automatically when a payment is recorded (Registrations &rarr; &#8942; &rarr; Record payment, or Financials).
                  </span>
                </div>
              </div>

              <div>
                <label htmlFor="golf_handicap" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Golf Handicap
                  {events.some(event => event.event_type === 'golf_tournament' && selectedEvents[event.id]) && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <input
                  type="number"
                  id="golf_handicap"
                  step="0.1"
                  value={formData.golf_handicap}
                  onChange={(e) => setFormData({ ...formData, golf_handicap: e.target.value })}
                  placeholder={events.some(event => event.event_type === 'golf_tournament' && selectedEvents[event.id]) ? "Required for golf (e.g., 18.5)" : "e.g., 18.5"}
                  className="mt-1 block w-full"
                />
              </div>

              <div>
                <label htmlFor="preferred_teammates" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Preferred Teammates
                </label>
                <textarea
                  id="preferred_teammates"
                  rows={2}
                  value={formData.preferred_teammates}
                  onChange={(e) => setFormData({ ...formData, preferred_teammates: e.target.value })}
                  placeholder="Names of preferred golf teammates"
                  className="mt-1 block w-full"
                />
              </div>

              {events.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Events Attending
                  </label>
                  <div className="space-y-2 bg-gray-50 dark:bg-night-700 p-4 rounded-md">
                    {events.map((event) => (
                      <div key={event.id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`select-event-${event.id}`}
                          checked={selectedEvents[event.id] || false}
                          onChange={() => handleEventToggle(event.id)}
                          className="h-4 w-4 text-primary-600 dark:text-primary-400 focus:ring-primary-500 border-gray-300 dark:border-night-600 rounded"
                        />
                        <label htmlFor={`select-event-${event.id}`} className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                          {event.event_name}
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Select which events this attendee is registered for
                  </p>
                </div>
              )}

              {events.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Child Counts by Event
                  </label>
                  <div className="space-y-3 bg-gray-50 dark:bg-night-700 p-4 rounded-md">
                    {events.map((event) => (
                      <div key={event.id} className="flex items-center justify-between">
                        <label htmlFor={`event-${event.id}`} className="text-sm text-gray-700 dark:text-gray-300">
                          {event.event_name}
                        </label>
                        <input
                          type="number"
                          id={`event-${event.id}`}
                          min="0"
                          value={registrationEvents[event.id] || 0}
                          onChange={(e) => handleChildCountChange(event.id, e.target.value)}
                          className="w-20 rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Enter the number of children attending each event
                  </p>
                </div>
              )}

              {/* Add another registrant to this registration's group */}
              <div className="border-t border-gray-200 dark:border-night-700 pt-4 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Add to this group</h3>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Add another registrant (e.g. a partner) to this registration's group. When you save,
                    everyone in the group is emailed an updated confirmation.
                  </p>
                </div>

                {groupAttendees.map((attendee, index) => (
                  <AttendeeCard
                    key={index}
                    attendee={attendee}
                    index={index}
                    events={events}
                    filteredContacts={getFilteredContactsFor(attendee.contactSearchTerm)}
                    onUpdate={updateGroupAttendee}
                    onRemove={removeGroupAttendee}
                    onCreateContact={handleCreateNewContactForGroup}
                    loading={loading}
                    canRemove={true}
                    label="New registrant"
                  />
                ))}

                <button
                  type="button"
                  onClick={addGroupAttendee}
                  className="w-full py-2 px-4 border-2 border-dashed border-gray-300 dark:border-night-600 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:border-gray-400 hover:text-gray-700 dark:text-gray-300 transition-colors"
                >
                  + Add registrant to group
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-night-800 border border-gray-300 dark:border-night-600 rounded-md shadow-sm hover:bg-gray-50 dark:bg-night-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {loading
                ? 'Saving...'
                : isCreateMode
                  ? (attendees.length > 1 ? `Create ${attendees.length} Registrations` : 'Create Registration')
                  : 'Save Changes'
              }
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
